import { CountryCombobox } from "@/components/ui/CountryCombobox";
import { PropertyList, PropertyItem } from "@/components/ui/property-list";
import { countryLabel, recordLabel, recordOptions } from "@/lib/employeeRecordLabels";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { DocumentDataPanel } from "@/components/dokumenti/DocumentDataPanel";
import { useEffect, useState, type ReactNode } from "react";

import { StructuredEvidence } from "@/components/dokumenti/StructuredEvidence";
import { Badge, type Ton } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { SkeletonTablica } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { datum } from "@/lib/format";
import { procitajGreske } from "@/lib/greske";
import {
  dohvatiIdentifikacijskeDokumente,
  dohvatiVozackuDozvolu,
  spremiIdentifikacijskiDokument,
  spremiVozackuDozvolu,
  type IdentifikacijskiDokument,
  type IdentifikacijskiDokumentInput,
  type VozackaDozvola,
  type VozackaDozvolaInput,
  type VrstaIdentifikacijskogDokumenta,
} from "@/lib/radnici";



const PRAZAN_DOKUMENT: IdentifikacijskiDokumentInput = {
  vrsta: "passport_identity_page",
  broj_dokumenta: null,
  izdavatelj: null,
  zemlja_izdavanja_iso2: null,
  datum_izdavanja: null,
  datum_isteka: null,
  napomena: null,
};

const PRAZNA_VOZACKA: VozackaDozvolaInput = {
  broj_dozvole: null,
  kategorije: [],
  datum_izdavanja: null,
  datum_isteka: null,
  zemlja_izdavanja_iso2: null,
  verificirana_u_rh: null,
  datum_verifikacije: null,
  napomena: null,
};

const control =
  "h-[var(--spacing-control)] rounded-[var(--radius-control)] border border-[var(--color-input-border)] bg-white px-3";

function poruka(error: unknown): string {
  return procitajGreske(error).opcenito[0] ?? "Spremanje nije uspjelo.";
}

function istek(vrijednost: string | null): { ton: Ton; tekst: string } {
  if (!vrijednost) return { ton: "idle", tekst: "Istek nije unesen" };
  const danas = new Date();
  danas.setHours(0, 0, 0, 0);
  const datumIsteka = new Date(`${vrijednost}T00:00:00`);
  const dana = Math.ceil((datumIsteka.getTime() - danas.getTime()) / 86_400_000);
  if (dana < 0) return { ton: "bad", tekst: `Isteklo ${datum(vrijednost)}` };
  if (dana <= 30) return { ton: "warn", tekst: `Ističe ${datum(vrijednost)}` };
  return { ton: "ok", tekst: `Vrijedi do ${datum(vrijednost)}` };
}

function Scan({ dokumentId }: { dokumentId?: string | null }) {
  return (
    <div className="avm-surface flex min-h-20 items-center justify-center rounded-[var(--radius-card)] border border-dashed border-[var(--color-input-border)] bg-[var(--color-muted)] px-4 text-sm text-[var(--color-muted-foreground)]">
      {dokumentId ? "Sken je učitan" : "Sken — nije učitan"}
    </div>
  );
}

export function DokumentiIdentitetTab({ radnikId, children }: { radnikId: string; children?: ReactNode }) {
  const { can } = useAuth();
  const location = useLocation();
  const [previewVersion, setPreviewVersion] = useState(0);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [dokumenti, setDokumenti] = useState<IdentifikacijskiDokument[]>([]);
  const [vozacka, setVozacka] = useState<VozackaDozvola | null>(null);
  const [dokument, setDokument] = useState<IdentifikacijskiDokumentInput>(PRAZAN_DOKUMENT);
  const [dokumentId, setDokumentId] = useState<string | null>(null);
  const [urediDokument, setUrediDokument] = useState(false);
  const [vozackaForma, setVozackaForma] = useState<VozackaDozvolaInput>(PRAZNA_VOZACKA);
  const [kategorije, setKategorije] = useState("");
  const [urediVozacku, setUrediVozacku] = useState(false);
  const [ucitava, setUcitava] = useState(true);
  const [sprema, setSprema] = useState(false);
  const [greska, setGreska] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    Promise.all([
      dohvatiIdentifikacijskeDokumente(radnikId, controller.signal),
      dohvatiVozackuDozvolu(radnikId, controller.signal).catch((error: unknown) => {
        if (axios.isAxiosError(error) && error.response?.status === 404) return null;
        throw error;
      }),
    ])
      .then(([noviDokumenti, novaVozacka]) => {
        setDokumenti(noviDokumenti);
        setVozacka(novaVozacka);
        const selectedId = location.hash.replace("#record-", "");
        const selected = noviDokumenti.find((item) => item.id === selectedId);
        setPreviewId(selected?.employee_document_id ?? (novaVozacka?.id === selectedId ? novaVozacka.employee_document_id : null) ?? noviDokumenti[0]?.employee_document_id ?? novaVozacka?.employee_document_id ?? null);
        if (novaVozacka) {
          setVozackaForma(novaVozacka);
          setKategorije(novaVozacka.kategorije.join(", "));
        }
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) setGreska(poruka(error));
      })
      .finally(() => {
        if (!controller.signal.aborted) setUcitava(false);
      });
    return () => controller.abort();
  }, [radnikId, location.hash]);

  function zapocniDokument(zapis?: IdentifikacijskiDokument): void {
    setDokument(zapis ? { ...zapis } : PRAZAN_DOKUMENT);
    if (zapis?.employee_document_id) setPreviewId(zapis.employee_document_id);
    setDokumentId(zapis?.id ?? null);
    setUrediDokument(true);
  }

  async function spremiDokument(): Promise<void> {
    setSprema(true);
    setGreska(null);
    try {
      const spremljeno = await spremiIdentifikacijskiDokument(
        radnikId,
        dokument,
        dokumentId ?? undefined,
      );
      setDokumenti((stari) =>
        dokumentId
          ? stari.map((zapis) => (zapis.id === spremljeno.id ? spremljeno : zapis))
          : [spremljeno, ...stari],
      );
      setUrediDokument(false);
      setPreviewVersion((version) => version + 1);
      setDokumentId(null);
    } catch (error) {
      setGreska(poruka(error));
    } finally {
      setSprema(false);
    }
  }

  async function spremiVozacku(): Promise<void> {
    setSprema(true);
    setGreska(null);
    try {
      const spremljeno = await spremiVozackuDozvolu(
        radnikId,
        {
          ...vozackaForma,
          kategorije: kategorije
            .split(",")
            .map((value) => value.trim().toUpperCase())
            .filter(Boolean),
          datum_verifikacije: vozackaForma.verificirana_u_rh
            ? vozackaForma.datum_verifikacije
            : null,
        },
        vozacka !== null,
      );
      setVozacka(spremljeno);
      setVozackaForma(spremljeno);
      setKategorije(spremljeno.kategorije.join(", "));
      setUrediVozacku(false);
      setPreviewVersion((version) => version + 1);
    } catch (error) {
      setGreska(poruka(error));
    } finally {
      setSprema(false);
    }
  }

  if (ucitava) return <><SkeletonTablica redaka={4} />{children}</>;
  if (greska && dokumenti.length === 0 && !vozacka) {
    return <><EmptyState naslov="Identitet nije dostupan" objasnjenje={greska} />{children}</>;
  }

  return (
    <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:overflow-hidden">
      <div aria-label="Zapisi identiteta" className="flex min-h-0 min-w-0 flex-col gap-4 lg:overflow-y-auto">
      {greska ? <p role="alert" className="text-[var(--color-status-bad-fg)]">{greska}</p> : null}
      <section className="avm-surface rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--color-border)] pb-3">
          <div><h2 className="font-semibold">Identifikacijski dokumenti</h2><p className="text-sm text-[var(--color-muted-foreground)]">Pristup identifikacijskim dokumentima evidentira se.</p></div>
          {can("dozvola", "create") ? <Button onClick={() => zapocniDokument()}>Dodaj dokument</Button> : null}
        </div>
        {urediDokument ? <DokumentForma vrijednosti={dokument} setVrijednosti={setDokument} sprema={sprema} onSpremi={() => void spremiDokument()} onOdustani={() => setUrediDokument(false)} /> : null}
        <div className="mt-4 grid gap-3">
          {dokumenti.length === 0 ? <p className="text-[var(--color-muted-foreground)]">Nema evidentiranih identifikacijskih dokumenata.</p> : dokumenti.map((zapis) => {
            const status = istek(zapis.datum_isteka);
            return <article id={`record-${zapis.id}`} key={zapis.id} className="avm-surface flex flex-col gap-4 rounded-[var(--radius-card)] border border-[var(--color-border)] p-4">
              <div className="flex flex-wrap items-center gap-2"><h3 className="font-semibold">{can("dokument", "read") && zapis.employee_document_id ? <button type="button" className="text-left underline" onClick={() => setPreviewId(zapis.employee_document_id)}>{recordLabel("identifikacijski_dokument", "vrsta", zapis.vrsta)}</button> : recordLabel("identifikacijski_dokument", "vrsta", zapis.vrsta)}</h3><Badge ton={status.ton}>{status.tekst}</Badge></div>
              <PropertyList><Podatak onAdd={can("dozvola", "update") ? () => zapocniDokument(zapis) : undefined} oznaka="Broj" vrijednost={zapis.broj_dokumenta} /><Podatak onAdd={can("dozvola", "update") ? () => zapocniDokument(zapis) : undefined} oznaka="Izdavatelj" vrijednost={zapis.izdavatelj} /><Podatak onAdd={can("dozvola", "update") ? () => zapocniDokument(zapis) : undefined} oznaka="Zemlja" vrijednost={countryLabel(zapis.zemlja_izdavanja_iso2)} /><Podatak onAdd={can("dozvola", "update") ? () => zapocniDokument(zapis) : undefined} oznaka="Datum izdavanja" vrijednost={zapis.datum_izdavanja ? datum(zapis.datum_izdavanja) : null} /><Podatak onAdd={can("dozvola", "update") ? () => zapocniDokument(zapis) : undefined} oznaka="Datum isteka" vrijednost={zapis.datum_isteka ? datum(zapis.datum_isteka) : null} /><Podatak onAdd={can("dozvola", "update") ? () => zapocniDokument(zapis) : undefined} oznaka="Napomena" vrijednost={zapis.napomena} /></PropertyList>
              <Scan dokumentId={zapis.dokument_id} />
              <StructuredEvidence kind="identifikacijski_dokument" recordId={zapis.id} />
              {can("dozvola", "update") ? <Button variant="outline" onClick={() => zapocniDokument(zapis)}>Uredi dokument</Button> : null}
            </article>;
          })}
        </div>
      </section>

      {children}
      <section id="vozacka-dozvola" tabIndex={-1} className="avm-surface scroll-mt-16 rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--color-border)] pb-3">
          <h2 className="font-semibold">{can("dokument", "read") && vozacka?.employee_document_id ? <button type="button" className="text-left underline" onClick={() => setPreviewId(vozacka.employee_document_id)}>Vozačka dozvola</button> : "Vozačka dozvola"}</h2>
          {(vozacka ? can("dozvola", "update") : can("dozvola", "create")) ? <Button variant="outline" onClick={() => setUrediVozacku((value) => !value)}>{urediVozacku ? "Odustani" : vozacka ? "Uredi vozačku" : "Unesi vozačku"}</Button> : null}
        </div>
        {urediVozacku ? <VozackaForma vrijednosti={vozackaForma} setVrijednosti={setVozackaForma} kategorije={kategorije} setKategorije={setKategorije} sprema={sprema} onSpremi={() => void spremiVozacku()} /> : vozacka ? <VozackaPrikaz zapis={vozacka} /> : <p className="mt-4 text-[var(--color-muted-foreground)]">Vozačka dozvola nije evidentirana.</p>}
      </section>
      </div>
      <section aria-label="Podaci i pregled identiteta" className="flex min-h-0 min-w-0 flex-col gap-3 lg:overflow-y-auto">
        {can("dokument", "read") && previewId ? <DocumentDataPanel key={previewId} radnikId={radnikId} documentId={previewId} refreshKey={previewVersion} showPreview onEdit={(record) => {
          if (record.kind === "vozacka_dozvola") setUrediVozacku(true);
          else { const selected = dokumenti.find((item) => item.id === record.id); if (selected) zapocniDokument(selected); }
        }} /> : <p className="p-3 text-sm text-muted-foreground">{can("dokument", "read") ? "Odaberite zapis s povezanim dokumentom." : "Nemate ovlast za pregled arhivskog dokumenta."}</p>}
      </section>
    </div>
  );
}

function Podatak({ oznaka, vrijednost, onAdd }: { oznaka: string; vrijednost: string | null | undefined; onAdd?: () => void }) {
  return <PropertyItem label={oznaka} onAdd={onAdd}>{vrijednost ?? "—"}</PropertyItem>;
}

function DokumentForma({ vrijednosti, setVrijednosti, sprema, onSpremi, onOdustani }: { vrijednosti: IdentifikacijskiDokumentInput; setVrijednosti: (value: IdentifikacijskiDokumentInput) => void; sprema: boolean; onSpremi: () => void; onOdustani: () => void }) {
  return <div className="mt-4 grid gap-4 rounded-[var(--radius-card)] bg-[var(--color-muted)] p-4 sm:grid-cols-2">
    <label className="flex flex-col gap-2"><span className="oznaka">Vrsta</span><select className={control} value={vrijednosti.vrsta} onChange={(event) => setVrijednosti({ ...vrijednosti, vrsta: event.target.value as VrstaIdentifikacijskogDokumenta })}>{recordOptions("identifikacijski_dokument", "vrsta").map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
    <Tekst oznaka="Broj" vrijednost={vrijednosti.broj_dokumenta} onPromjena={(value) => setVrijednosti({ ...vrijednosti, broj_dokumenta: value })} />
    <Tekst oznaka="Izdavatelj" vrijednost={vrijednosti.izdavatelj} onPromjena={(value) => setVrijednosti({ ...vrijednosti, izdavatelj: value })} />
    <CountryCombobox label="Zemlja izdavanja" value={vrijednosti.zemlja_izdavanja_iso2 ?? ""} onChange={value => setVrijednosti({ ...vrijednosti, zemlja_izdavanja_iso2: value || null })} disabled={sprema} />
    <DatumPolje oznaka="Datum izdavanja" vrijednost={vrijednosti.datum_izdavanja} onPromjena={(value) => setVrijednosti({ ...vrijednosti, datum_izdavanja: value })} />
    <DatumPolje oznaka="Datum isteka" vrijednost={vrijednosti.datum_isteka} onPromjena={(value) => setVrijednosti({ ...vrijednosti, datum_isteka: value })} />
    <Tekst oznaka="Napomena" vrijednost={vrijednosti.napomena} onPromjena={(value) => setVrijednosti({ ...vrijednosti, napomena: value })} />
    <div className="flex gap-3 sm:col-span-2"><Button disabled={sprema} onClick={onSpremi}>{sprema ? "Spremanje…" : "Spremi dokument"}</Button><Button variant="outline" onClick={onOdustani}>Odustani</Button></div>
  </div>;
}

function VozackaForma({ vrijednosti, setVrijednosti, kategorije, setKategorije, sprema, onSpremi }: { vrijednosti: VozackaDozvolaInput; setVrijednosti: (value: VozackaDozvolaInput) => void; kategorije: string; setKategorije: (value: string) => void; sprema: boolean; onSpremi: () => void }) {
  return <div className="mt-4 grid gap-4 sm:grid-cols-2">
    <Tekst oznaka="Broj vozačke dozvole" vrijednost={vrijednosti.broj_dozvole} onPromjena={(value) => setVrijednosti({ ...vrijednosti, broj_dozvole: value })} />
    <label className="flex flex-col gap-2"><span className="oznaka">Kategorije</span><input className={control} value={kategorije} onChange={(event) => setKategorije(event.target.value)} placeholder="B, C, CE" /></label>
    <CountryCombobox label="Zemlja izdavanja" value={vrijednosti.zemlja_izdavanja_iso2 ?? ""} onChange={value => setVrijednosti({ ...vrijednosti, zemlja_izdavanja_iso2: value || null })} disabled={sprema} />
    <DatumPolje oznaka="Datum izdavanja" vrijednost={vrijednosti.datum_izdavanja} onPromjena={(value) => setVrijednosti({ ...vrijednosti, datum_izdavanja: value })} />
    <DatumPolje oznaka="Datum isteka" vrijednost={vrijednosti.datum_isteka} onPromjena={(value) => setVrijednosti({ ...vrijednosti, datum_isteka: value })} />
    <label className="flex items-center gap-2 self-end pb-3"><input type="checkbox" className="size-5 min-h-0" checked={vrijednosti.verificirana_u_rh === true} onChange={(event) => setVrijednosti({ ...vrijednosti, verificirana_u_rh: event.target.checked, datum_verifikacije: event.target.checked ? vrijednosti.datum_verifikacije : null })} /><span>Provjerena u RH</span></label>
    {vrijednosti.verificirana_u_rh ? <DatumPolje oznaka="Datum provjere" vrijednost={vrijednosti.datum_verifikacije} onPromjena={(value) => setVrijednosti({ ...vrijednosti, datum_verifikacije: value })} /> : null}
    <Tekst oznaka="Napomena" vrijednost={vrijednosti.napomena} onPromjena={(value) => setVrijednosti({ ...vrijednosti, napomena: value })} />
    <div className="sm:col-span-2"><Button disabled={sprema || (vrijednosti.verificirana_u_rh === true && !vrijednosti.datum_verifikacije)} onClick={onSpremi}>{sprema ? "Spremanje…" : "Spremi vozačku"}</Button></div>
  </div>;
}

function VozackaPrikaz({ zapis }: { zapis: VozackaDozvola }) {
  const status = istek(zapis.datum_isteka);
  return <div id={`record-${zapis.id}`} className="mt-4 flex flex-col gap-4"><StructuredEvidence kind="vozacka_dozvola" recordId={zapis.id} /><div className="flex flex-wrap items-center gap-2"><p className="font-medium">{zapis.broj_dozvole ?? "Broj nije unesen"}</p><Badge ton={status.ton}>{status.tekst}</Badge></div><PropertyList><Podatak oznaka="Kategorije" vrijednost={zapis.kategorije.join(", ") || null} /><Podatak oznaka="Datum izdavanja" vrijednost={zapis.datum_izdavanja ? datum(zapis.datum_izdavanja) : null} /><Podatak oznaka="Zemlja" vrijednost={countryLabel(zapis.zemlja_izdavanja_iso2)} /><Podatak oznaka="Provjerena u RH" vrijednost={zapis.verificirana_u_rh === true ? "Da" : zapis.verificirana_u_rh === false ? "Ne" : null} /><Podatak oznaka="Datum provjere" vrijednost={zapis.datum_verifikacije ? datum(zapis.datum_verifikacije) : null} /><Podatak oznaka="Napomena" vrijednost={zapis.napomena} /></PropertyList><ul aria-label="Rokovi kategorija">{zapis.kategorije_detalji?.map((category) => <li key={category.kategorija}>{category.kategorija} · izdana {category.issued_on ? datum(category.issued_on) : "—"} · vrijedi do {category.expires_on ? datum(category.expires_on) : "—"}</li>)}</ul><Scan dokumentId={zapis.dokument_id} /></div>;
}

function Tekst({ oznaka, vrijednost, onPromjena, maxLength }: { oznaka: string; vrijednost: string | null; onPromjena: (value: string | null) => void; maxLength?: number }) {
  return <label className="flex flex-col gap-2"><span className="oznaka">{oznaka}</span><input className={control} maxLength={maxLength} value={vrijednost ?? ""} onChange={(event) => onPromjena(event.target.value || null)} /></label>;
}

function DatumPolje({ oznaka, vrijednost, onPromjena }: { oznaka: string; vrijednost: string | null; onPromjena: (value: string | null) => void }) {
  return <label className="flex flex-col gap-2"><span className="oznaka">{oznaka}</span><input type="date" className={control} value={vrijednost ?? ""} onChange={(event) => onPromjena(event.target.value || null)} /></label>;
}
